import type { ClosedTranche, FundSnapshot } from '../../core/types.ts';
import { price, relTime, toneClass, usd, usdSigned } from '../lib/format.ts';
import { Empty, Panel, Pill } from './ui.tsx';

const REASON_LABEL: Record<ClosedTranche['reason'], { text: string; tone: 'up' | 'down' | 'neutral' }> = {
  take_profit: { text: 'target hit', tone: 'up' },
  trail_stop: { text: 'breakeven', tone: 'neutral' },
  stop_loss: { text: 'stopped', tone: 'down' },
  liquidation: { text: 'liquidated', tone: 'down' },
  manual: { text: 'manual', tone: 'neutral' },
};

function heldFor(c: ClosedTranche): string {
  const mins = Math.max(1, Math.round((c.closedAt - c.openedAt) / 60000));
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ${mins % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

export function TradeHistory({ funds, now }: { funds: FundSnapshot[]; now: number }) {
  const rows = funds
    .flatMap((f) => f.recentCloses.map((c) => ({ c, f })))
    .sort((a, b) => b.c.closedAt - a.c.closedAt)
    .slice(0, 40);

  const realized = funds.reduce((s, f) => s + f.lifetime.realizedPnlUsd, 0);

  return (
    <Panel
      title="Closed trades"
      subtitle="Every completed round trip, net of trading and borrowing costs."
      right={<Pill tone={realized >= 0 ? 'up' : 'down'}>{usdSigned(realized)} realized</Pill>}
      bodyClassName=""
    >
      {rows.length === 0 ? (
        <Empty>No trades have closed yet.</Empty>
      ) : (
        <div className="max-h-[320px] overflow-auto">
          <table className="w-full min-w-[640px] text-left">
            <thead className="sticky top-0 bg-panel">
              <tr className="text-[10px] tracking-wide text-dim uppercase">
                <th className="px-3 py-2 font-medium">Closed</th>
                <th className="px-3 py-2 font-medium">Trade</th>
                <th className="px-3 py-2 text-right font-medium">Entry</th>
                <th className="px-3 py-2 text-right font-medium">Exit</th>
                <th className="px-3 py-2 text-right font-medium">Held</th>
                <th className="px-3 py-2 text-right font-medium">Net P&L</th>
                <th className="px-3 py-2 text-right font-medium">Outcome</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ c, f }) => {
                const reason = REASON_LABEL[c.reason];
                return (
                  <tr key={c.id} className="border-t border-edge/70">
                    <td className="num px-3 py-2 text-[11px] text-dim">{relTime(c.closedAt, now)}</td>
                    <td className="px-3 py-2">
                      <span className="flex items-center gap-1.5">
                        <img src={f.logo} alt="" className="size-4 rounded-full" width={16} height={16} />
                        <span className="text-[12px] font-medium">{c.symbol}</span>
                        {/* The arrow needs a gap: flush against the number it
                            reads as a leading digit ("↓2.0x" looks like 12.0x). */}
                        <span
                          className={`flex items-center gap-0.5 text-[10px] ${c.side === 'short' ? 'text-short' : 'text-long'}`}
                        >
                          <span aria-hidden>{c.side === 'short' ? '↓' : '↑'}</span>
                          <span className="sr-only">{c.side}</span>
                          {c.leverage.toFixed(1)}x
                        </span>
                      </span>
                    </td>
                    <td className="num px-3 py-2 text-right text-[12px] text-muted">{price(c.entryPrice)}</td>
                    <td className="num px-3 py-2 text-right text-[12px] text-muted">{price(c.exitPrice)}</td>
                    <td className="num px-3 py-2 text-right text-[11px] text-dim">{heldFor(c)}</td>
                    <td className={`num px-3 py-2 text-right text-[12px] font-semibold ${toneClass(c.realizedPnlUsd)}`}>
                      {usdSigned(c.realizedPnlUsd)}
                      <span
                        className="ml-1 text-[9px] font-normal text-dim"
                        title={`fees ${usd(c.feesUsd)} · borrowing ${usdSigned(c.fundingAccruedUsd)} · margin ${usd(c.collateralUsd)}`}
                      >
                        ⓘ
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Pill tone={reason.tone}>{reason.text}</Pill>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
